import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { Breadcrumb, Button, Dropdown, Select, Tooltip } from 'antd';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { logout } from 'redux/auth/actions';
import { useAppDisPatch } from 'utils/common';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { RoleBadge, RoleBadgeKind, ROLE_BADGE_LABEL } from 'components/role-badge';
import UserAvatar from 'components/user-avatar';
import { allowsAudience, findNav, HOME_ITEM, isSearchableFor, NAV_GROUPS } from '../nav-config';
import styles from './styles.module.scss';

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

// Bỏ dấu để gõ "cham cong" vẫn ra "Chấm công".
const fold = (text: string) =>
  text.normalize('NFD').replace(/\p{M}/gu, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

const Header = ({ collapsed, onToggle }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDisPatch();
  const {
    displayName,
    avatar,
    email,
    department,
    isAdmin,
    isMod,
    canImportData,
    canViewOnlineReward,
    canUseEquipmentQuote,
    audience,
  } = useCurrentRoles();
  const isStudioViewer = audience === 'STUDIO';
  const match = findNav(location.pathname);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<any>(null);

  // Ctrl+K / Cmd+K: nhảy tới ô tìm chức năng từ bất kỳ đâu.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const searchOptions = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        label: group.label,
        options: group.items
          .filter((item) =>
            isSearchableFor(
              item,
              isAdmin,
              { dataImport: canImportData, onlineReward: canViewOnlineReward, equipmentQuote: canUseEquipmentQuote },
              audience
            )
          )
          .map((item) => ({
            value: item.key,
            label: (
              <div className={styles.searchOption}>
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </div>
            ),
            search: fold(`${item.label} ${group.label} ${item.description} ${item.keywords || ''}`),
          })),
      })).filter((group) => group.options.length > 0),
    [isAdmin, canImportData, canViewOnlineReward, canUseEquipmentQuote, audience]
  );

  const crumbs = useMemo(() => {
    // Quản lý Studio không có trang "Tổng quan": mục đầu là "Studio của tôi".
    const list: { title: React.ReactNode }[] = [
      {
        title: isStudioViewer ? (
          <Link to="/studio">Studio của tôi</Link>
        ) : (
          <Link to="/">{HOME_ITEM.label}</Link>
        ),
      },
    ];
    if (match?.group) list.push({ title: match.group.label });
    // Bỏ mục cha khi người xem không được thấy mục đó (vd. Quản lý Studio với danh sách Studio).
    if (match?.parentItem && allowsAudience(match.parentItem, isAdmin, audience)) {
      list.push({ title: <Link to={`/${match.parentItem.key}`}>{match.parentItem.label}</Link> });
    }
    if (match?.item && match.item !== HOME_ITEM) list.push({ title: match.item.label });
    return list;
  }, [match, isStudioViewer, isAdmin, audience]);

  // Huỷ phiên trên server trước (trước đây chỉ xoá localStorage nên phiên server vẫn sống),
  // rồi về /login với thông báo "Bạn đã đăng xuất."
  const handleLogout = () => {
    dispatch(logout(navigate, 'loggedOut'));
  };

  const userMenu: MenuProps['items'] = [
    {
      key: 'who',
      disabled: true,
      label: (
        <div className={styles.userMenuHead}>
          <strong>{displayName || 'Tài khoản'}</strong>
          {email && <small>{email}</small>}
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      danger: true,
      onClick: handleLogout,
    },
  ];

  // Huy hiệu vai trò (30/09/2026, cùng bộ hình với app): Quản lý = MOD phòng QL Vận Hành
  // hoặc Quản lý Studio; Mod = MOD còn lại (Quản lý tổng). Rê chuột để xem chữ.
  const isOpsManager =
    String(department || '')
      .trim()
      .toUpperCase() === 'MANAGER';
  const roleBadge: RoleBadgeKind | null = isAdmin
    ? 'ADMIN'
    : isStudioViewer || (isMod && isOpsManager)
    ? 'MANAGER'
    : isMod
    ? 'MOD'
    : null;
  const roleDetail = isAdmin
    ? 'Quản trị cao nhất HP Media'
    : isStudioViewer
    ? 'Quản lý Studio'
    : isOpsManager
    ? 'Quản lý vận hành'
    : 'Quản lý tổng';

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <Tooltip title={collapsed ? 'Mở rộng thanh bên' : 'Thu gọn thanh bên'}>
          <Button
            type="text"
            aria-label="Thu gọn/mở rộng thanh bên"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onToggle}
          />
        </Tooltip>
        <Breadcrumb items={crumbs} className={styles.breadcrumb} />
      </div>

      <div className={styles.headerRight}>
        <Select
          ref={searchRef}
          showSearch
          value={null}
          open={searchOpen}
          onOpenChange={setSearchOpen}
          className={styles.search}
          placeholder={
            <span>
              <SearchOutlined /> Tìm chức năng… <kbd>Ctrl K</kbd>
            </span>
          }
          options={searchOptions}
          filterOption={(input, option: any) => !!option?.search?.includes(fold(input))}
          onSelect={(value: string | null) => {
            setSearchOpen(false);
            searchRef.current?.blur();
            if (value !== null) navigate(`/${value}`);
          }}
          popupMatchSelectWidth={380}
          notFoundContent="Không có chức năng phù hợp"
          suffixIcon={null}
        />
        <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
          <button type="button" className={styles.userButton}>
            <UserAvatar size={32} src={avatar} alt="" />
            <span className={styles.userText}>
              <strong>{displayName || '…'}</strong>
            </span>
            {/* Ngoài userText để màn hẹp (ẩn tên) vẫn thấy vai trò */}
            {roleBadge && (
              <Tooltip title={`${ROLE_BADGE_LABEL[roleBadge]} · ${roleDetail}`}>
                <span className={styles.roleBadge}>
                  <RoleBadge kind={roleBadge} size={18} />
                </span>
              </Tooltip>
            )}
          </button>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
